/**
 * Import des saisies depuis un fichier CSV ou XLSX.
 *
 * **Rien ne part sans avoir été vu.** Le fichier est lu dans le navigateur, chaque ligne validée
 * et montrée avec sa faute s'il y en a une, et seules les lignes cochées sont envoyées. C'est le
 * même parti pris que l'import de notes, pour la même raison : un import muet qui écrit
 * quatre-vingts lignes se découvre trop tard.
 *
 * Une nuance propre aux chiffres : la contrainte d'unicité fait qu'un réimport **réécrit** au
 * lieu de dupliquer. Les périodes déjà saisies sont donc signalées — non comme des doublons à
 * écarter, mais comme des valeurs qui vont être remplacées.
 */

import React, { useState, useMemo, useRef } from 'react';
import { Upload, Download, AlertCircle, Loader } from 'lucide-react';
import {
  lireFichier, analyserLignes, modeleCsv, colonnesDuJeu, JEUX, EXTENSIONS, LIGNES_MAX
} from '../../utils/lireSaisiesConsolide';

/** Le domaine, dans le nom du fichier modèle. */
const NOMS_FICHIER = {
  backOffice: 'back-office',
  objectifs: 'objectifs',
  couts: 'couts',
  budget: 'budgets'
};

/**
 * `jeu` désigne le domaine importé — back-office, objectifs, coûts ou budgets.
 *
 * Chaque domaine a son propre fichier, avec ses seules colonnes. Un modèle commun aux quatre
 * inviterait à remplir des colonnes d'un autre domaine, et l'import écraserait alors ce qu'on
 * n'avait pas l'intention de toucher.
 */
function SaisiesImport({ saisiesExistantes, onEnregistrer, enregistrement, erreur, jeu = 'backOffice', cibles = null }) {
  const COLONNES = colonnesDuJeu(jeu);
  /** Les jeux par destination — coûts, budgets — portent une colonne de plus, et une clé de plus. */
  const parCible = Boolean(JEUX[jeu].parCible);
  /** Intitulé lisible d'une destination, pour l'aperçu : la clé technique n'y apprend rien. */
  const nommerCible = (cle) => (cibles || []).find(c => c.cle === cle)?.titre || cle;
  const VALEURS = JEUX[jeu].valeurs;
  const [feuilles, setFeuilles] = useState(null);
  const [feuilleChoisie, setFeuilleChoisie] = useState(0);
  const [exclues, setExclues] = useState(new Set());
  const [erreurLecture, setErreurLecture] = useState('');
  const champFichier = useRef(null);

  const feuille = feuilles?.[feuilleChoisie];
  const analyse = useMemo(
    () => (feuille ? analyserLignes(feuille.entetes, feuille.lignes, jeu, cibles) : null),
    [feuille, jeu, cibles]
  );

  /** Périodes déjà en base : elles seront remplacées, ce qui n'est pas la même chose qu'un doublon. */
  const dejaSaisies = useMemo(() => new Set(
    (saisiesExistantes || []).map(s => (
      // Même clé que l'analyse : sans la destination, un coût SEO déjà saisi ferait annoncer
      // « remplacée » à une ligne Google Ads du même mois, qui ne remplace rien.
      parCible ? `${s.cible}|${s.granularite}|${s.debut}` : `${s.granularite}|${s.debut}`
    ))
  ), [saisiesExistantes, parCible]);

  const traiterFichier = async (fichier) => {
    setErreurLecture('');
    setExclues(new Set());
    try {
      const lues = await lireFichier(fichier);
      setFeuilles(lues);
      setFeuilleChoisie(0);
    } catch (e) {
      setErreurLecture(e.message);
      setFeuilles(null);
    }
  };

  const telechargerModele = () => {
    const url = URL.createObjectURL(new Blob([modeleCsv(jeu, cibles)], { type: 'text/csv;charset=utf-8' }));
    const lien = document.createElement('a');
    lien.href = url;
    // Le nom portait « back-office » quel que soit le domaine : quatre fichiers homonymes
    // dans le dossier des téléchargements, et aucun moyen de les distinguer.
    lien.download = `modele-import-${NOMS_FICHIER[jeu] || jeu}.csv`;
    lien.click();
    URL.revokeObjectURL(url);
  };

  const retenues = (analyse?.lignes || []).filter(l => !l.faute && !exclues.has(l.numero));

  const basculer = (numero) => setExclues(actuelles => {
    const suivantes = new Set(actuelles);
    if (suivantes.has(numero)) suivantes.delete(numero); else suivantes.add(numero);
    return suivantes;
  });

  const importer = () => {
    const lignes = retenues.map(({ numero, faute, exemple, ...reste }) => reste);
    onEnregistrer(lignes, () => { setFeuilles(null); setExclues(new Set()); });
  };

  const fautives = (analyse?.lignes || []).filter(l => l.faute && !l.exemple).length;
  const exemples = (analyse?.lignes || []).filter(l => l.exemple).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button
          onClick={() => champFichier.current?.click()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px',
            borderRadius: '8px', border: '1px dashed #cbd5e1', background: '#fff',
            cursor: 'pointer', fontSize: '0.875rem', color: '#374151'
          }}
        >
          <Upload size={15} /> Choisir un fichier ({EXTENSIONS.join(', ')})
        </button>
        <input
          ref={champFichier}
          type="file"
          accept={EXTENSIONS.join(',')}
          onChange={e => e.target.files?.[0] && traiterFichier(e.target.files[0])}
          style={{ display: 'none' }}
        />
        <button
          onClick={telechargerModele}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px',
            borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff',
            cursor: 'pointer', fontSize: '0.875rem', color: '#374151'
          }}
        >
          <Download size={15} /> Modèle CSV
        </button>

        {feuilles && feuilles.length > 1 && (
          <select
            value={feuilleChoisie}
            onChange={e => { setFeuilleChoisie(Number(e.target.value)); setExclues(new Set()); }}
            style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}
          >
            {feuilles.map((f, i) => <option key={i} value={i}>{f.nom}</option>)}
          </select>
        )}
      </div>

      {/*
        Ce que contient chaque colonne, colonne par colonne.

        La phrase précédente tenait en trois lignes et n'expliquait que la granularité. « debut »
        restait une énigme — le mot ne dit pas qu'il s'agit du **premier jour** de la période, ni
        qu'il doit tomber sur un 1er de mois ou un lundi, règle que le serveur fait pourtant
        respecter. Et la liste des destinations n'était nulle part : il fallait deviner les clés.
      */}
      <div style={{
        margin: '0 0 14px', padding: '12px 14px', borderRadius: '10px',
        background: '#f8fafc', border: '1px solid #e5e7eb',
        fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.6
      }}>
        <div style={{ fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
          Colonnes attendues
        </div>
        <ul style={{ margin: 0, paddingLeft: '18px' }}>
          <li>
            <code>granularite</code> — <strong>mois</strong>, <strong>semaine</strong> ou{' '}
            <strong>jour</strong>. Elle dit ce que couvre la ligne : un montant mensuel se saisit
            une fois, pas trente fois.
          </li>
          {parCible && (
            <li>
              <code>destination</code> — le levier auquel le montant se rattache. La liste
              complète figure ci-dessous ; la clé comme l'intitulé sont acceptés.
            </li>
          )}
          <li>
            <code>debut</code> — le <strong>premier jour</strong> de la période, au format
            JJ/MM/AAAA. Un mois commence un <strong>1er</strong> (01/03/2026 pour mars), une
            semaine un <strong>lundi</strong>, un jour est la date elle-même. Il n'y a pas de
            colonne de fin : la granularité suffit à la déduire.
          </li>
          {VALEURS.map(v => (
            <li key={v.colonne}>
              <code>{v.colonne}</code> — {v.titre}
              {v.entier ? ', nombre entier' : ''}. Une cellule vide laisse la valeur inchangée.
            </li>
          ))}
        </ul>
        <div style={{ marginTop: '8px' }}>
          Une période déjà saisie est <strong>remplacée</strong>, jamais dupliquée.
        </div>
      </div>

      {/*
        Les destinations, en toutes lettres.

        Elles sont propres à la propriété — un compte sans Microsoft Ads n'en a pas la ligne —
        et aucune liste écrite à l'avance ne pourrait les énumérer. On rend donc le catalogue
        que le serveur vient d'envoyer, celui-là même que la grille de saisie propose.
      */}
      {parCible && (cibles || []).length > 0 && (
        <div style={{
          margin: '0 0 14px', padding: '12px 14px', borderRadius: '10px',
          background: '#fff', border: '1px solid #e5e7eb', fontSize: '0.8125rem'
        }}>
          <div style={{ fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
            Destinations acceptées ({cibles.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {cibles.map(c => (
              <span
                key={c.cle}
                title={`Écrivez « ${c.cle} » ou « ${c.titre} »`}
                style={{
                  display: 'inline-flex', alignItems: 'baseline', gap: '6px',
                  padding: '3px 9px', borderRadius: '999px',
                  background: '#f1f5f9', color: '#334155'
                }}
              >
                {c.titre}
                <code style={{ color: '#64748b', fontSize: '0.75rem' }}>{c.cle}</code>
              </span>
            ))}
          </div>
        </div>
      )}

      {erreurLecture && (
        <div style={{ padding: '9px 11px', background: '#fef2f2', borderRadius: '6px', fontSize: '0.8125rem', color: '#b91c1c', marginBottom: '12px' }}>
          {erreurLecture}
        </div>
      )}

      {analyse?.colonnesManquantes?.length > 0 && (
        <div style={{ padding: '9px 11px', background: '#fef2f2', borderRadius: '6px', fontSize: '0.8125rem', color: '#b91c1c', marginBottom: '12px' }}>
          Colonnes manquantes : {analyse.colonnesManquantes.join(', ')}
        </div>
      )}

      {analyse?.lignes?.length > 0 && (
        <>
          <div style={{ fontSize: '0.8125rem', color: '#4b5563', marginBottom: '8px' }}>
            {analyse.lignes.length} ligne(s) lue(s) · <strong>{retenues.length}</strong> à importer
            {fautives > 0 && <> · <span style={{ color: '#b91c1c' }}>{fautives} en faute</span></>}
            {exemples > 0 && <> · {exemples} ligne(s) d'exemple ignorée(s)</>}
            {analyse.tronque && <> · <span style={{ color: '#b45309' }}>fichier tronqué à {LIGNES_MAX} lignes</span></>}
          </div>

          <div style={{ maxHeight: '38vh', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: '#faf7f5' }}>
                  <th style={{ width: '34px' }} />
                  <th style={{ textAlign: 'left', padding: '7px 9px', color: '#7c2d12' }}>Ligne</th>
                  <th style={{ textAlign: 'left', padding: '7px 9px', color: '#7c2d12' }}>Période</th>
                  {VALEURS.map(v => (
                      <th key={v.cle} style={{ textAlign: 'right', padding: '7px 9px', color: '#7c2d12' }}>
                        {v.titre.charAt(0).toUpperCase() + v.titre.slice(1)}
                      </th>
                    ))}
                    </tr>
              </thead>
              <tbody>
                {analyse.lignes.map(l => {
                  const remplace = !l.faute && dejaSaisies.has(
                parCible ? `${l.cible}|${l.granularite}|${l.debut}` : `${l.granularite}|${l.debut}`
              );
                  return (
                    <tr key={l.numero} style={{ borderTop: '1px solid #f1f5f9', background: l.faute ? '#fffbfb' : 'transparent' }}>
                      <td style={{ textAlign: 'center' }}>
                        {!l.faute && (
                          <input
                            type="checkbox"
                            checked={!exclues.has(l.numero)}
                            onChange={() => basculer(l.numero)}
                          />
                        )}
                      </td>
                      <td style={{ padding: '5px 9px', color: '#9ca3af' }}>{l.numero}</td>
                      <td style={{ padding: '5px 9px', color: l.faute ? '#b91c1c' : '#1f2937' }}>
                        {l.faute
                          ? <span style={{ fontStyle: l.exemple ? 'italic' : 'normal', color: l.exemple ? '#9ca3af' : '#b91c1c' }}>{l.faute}</span>
                          : <>
                            {l.granularite.toLowerCase()} · {l.debut}
              {parCible && l.cible && <> · {nommerCible(l.cible)}</>}
                            {remplace && <span title="Cette période est déjà saisie : sa valeur sera remplacée." style={{ marginLeft: '6px', color: '#b45309', cursor: 'help' }}>remplace</span>}
                          </>}
                      </td>
                      {VALEURS.map(v => (
                        <td key={v.cle} style={{ padding: '5px 9px', textAlign: 'right' }}>{l[v.cle] ?? ''}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {erreur && (
        <div style={{ marginTop: '10px', padding: '8px 10px', background: '#fef2f2', borderRadius: '6px', fontSize: '0.8125rem', color: '#b91c1c', display: 'flex', gap: '6px' }}>
          <AlertCircle size={15} style={{ flexShrink: 0 }} /> <span>{erreur}</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
        <button
          onClick={importer}
          disabled={retenues.length === 0 || enregistrement}
          style={{
            padding: '9px 18px', borderRadius: '8px', border: 'none', fontSize: '0.875rem', fontWeight: 500,
            background: retenues.length === 0 ? '#e5e7eb' : '#7c2d12',
            color: retenues.length === 0 ? '#9ca3af' : '#fff',
            cursor: retenues.length === 0 ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '7px'
          }}
        >
          {enregistrement && <Loader size={14} className="spin" />}
          Importer {retenues.length > 0 ? `${retenues.length} période(s)` : ''}
        </button>
      </div>
    </div>
  );
}

export default SaisiesImport;
